/**
 * Construtores de payload para APIs e Corel
 *
 * Consolidação das funções de construção de payloads
 * Origem: linhas 370-460, 462-670, 1515-1543 do useSaveController.ts
 */

import { generateSafeFileName } from "../../../utils/fileNaming";
import {
	normalizeContraction,
	contractionToNumber,
} from "../../../utils/contractionNormalizer";
import { Keys, readWithAliases } from "../../../lib/storage/keys";
import {
	deriveObjectsFromWorkingData,
	normalizeToHHmm,
	calcularHorarioEntrega,
	extractFileName,
} from "./saveDataTransformers";
import { logger, sendToTerminal } from "../../../lib/terminalLogger";

/**
 * Constrói payload de metadados completo para o Corel
 *
 * @param workingData - Dados do salvamento
 * @param metadataSnapshotRef - Snapshot de metadados do Corel
 * @param lastSaveMetaRef - Ref para armazenar metadados do último salvamento
 * @param role - Role do usuário
 * @returns Payload de metadados
 */
export function buildMetadataPayload(
	workingData: any,
	metadataSnapshotRef: any,
	lastSaveMetaRef: any,
	role?: string,
) {
	const snapshot = metadataSnapshotRef;
	const resultBlock =
		snapshot && typeof snapshot === "object"
			? ((snapshot as any).result ?? (snapshot as any).data ?? snapshot)
			: null;

	const objetosRaw = Array.isArray(resultBlock?.objetos)
		? resultBlock.objetos
		: Array.isArray(resultBlock?.data?.objetos)
			? resultBlock.data.objetos
			: null;

	const objetosSource =
		Array.isArray(objetosRaw) && objetosRaw.length > 0
			? objetosRaw
			: deriveObjectsFromWorkingData(workingData);

	const objetos = objetosSource.map((obj: any, index: number) => {
		const visuais =
			obj.visuais && typeof obj.visuais === "object" ? obj.visuais : {};
		const pageIndex = obj.page ?? visuais.page ?? null;
		return {
			staticId:
				obj.staticId ?? obj.StaticID ?? obj.id ?? obj.Id ?? `obj-${index + 1}`,
			guid: obj.guid ?? obj.Guid ?? null,
			nome: obj.nome ?? obj.Nome ?? `Objeto ${index + 1}`,
			baseName:
				obj.baseName ??
				obj.BaseName ??
				obj.nome ??
				obj.Nome ??
				`Objeto ${index + 1}`,
			multiplicador:
				obj.multiplicador ?? obj.Multiplicador ?? obj.quantity ?? null,
			contracao: obj.contracao ?? obj.Contracao ?? obj.contraction ?? null,
			exportIndex: obj.exportIndex ?? obj.ExportIndex ?? null,
			largura: obj.largura ?? obj.Largura ?? visuais.width ?? obj.width ?? null,
			altura: obj.altura ?? obj.Altura ?? visuais.height ?? obj.height ?? null,
			tipo: obj.tipo ?? obj.Tipo ?? visuais.tipo ?? visuais.type ?? null,
			layer: obj.layer ?? obj.Layer ?? visuais.layer ?? null,
			page:
				typeof pageIndex === "number"
					? pageIndex
					: typeof pageIndex === "string"
						? Number(pageIndex)
						: null,
		};
	});

	let paginas: Array<{ nome: string; index: number }> = [];
	if (Array.isArray(resultBlock?.paginas) && resultBlock.paginas.length > 0) {
		paginas = resultBlock.paginas.map((page: any, index: number) => ({
			nome:
				page?.nome ??
				page?.Nome ??
				page?.name ??
				`Página_${String(page?.index ?? index + 1).padStart(3, "0")}`,
			index:
				typeof page?.index === "number"
					? page.index
					: typeof page?.numero === "number"
						? page.numero
						: typeof page?.page === "number"
							? page.page
							: index + 1,
		}));
	} else if (objetos.length) {
		const map = new Map<number, { nome: string; index: number }>();
		objetos.forEach((obj: any) => {
			if (obj.page != null && !Number.isNaN(Number(obj.page))) {
				const pageIndex = Number(obj.page);
				if (!map.has(pageIndex)) {
					map.set(pageIndex, {
						index: pageIndex,
						nome: `Página_${String(pageIndex).padStart(3, "0")}`,
					});
				}
			}
		});
		paginas = Array.from(map.values()).sort((a, b) => a.index - b.index);
	}

	const documentoSrc = resultBlock?.documento ?? {};
	const pedidoSrc = resultBlock?.pedido ?? {};

	const documento = {
		arquivo: documentoSrc?.arquivo ?? workingData.caminho_arquivo ?? "",
		versaoCorel: documentoSrc?.versaoCorel ?? "25.0",
		totalPaginas:
			documentoSrc?.totalPaginas ?? (paginas.length || objetos.length || 1),
		usuario: documentoSrc?.usuario ?? workingData.usuario ?? role ?? "usuario",
		diretorioOriginal:
			documentoSrc?.diretorioOriginal ?? workingData.diretorios?.interno ?? "",
		entrega:
			documentoSrc?.entrega ?? workingData.configuracoes?.entrega ?? "Hoje",
	};

	const pedido = {
		pedidoId: pedidoSrc?.pedidoId ?? workingData.pedido?.orc_id ?? null,
		itemId: pedidoSrc?.itemId ?? workingData.pedido?.oit_id ?? null,
		cliente:
			pedidoSrc?.cliente ??
			workingData.cliente?.nome ??
			workingData.cliente?.nome_fantasia ??
			"",
		produto: pedidoSrc?.produto ?? workingData.produto?.nome ?? "",
		contracao:
			pedidoSrc?.contracao ?? workingData.configuracoes?.contracao ?? null,
	};

	const log = Array.isArray(resultBlock?.log) ? resultBlock.log : [];

	// Armazenar snapshot no lastSaveMetaRef
	if (lastSaveMetaRef) {
		if (!lastSaveMetaRef.current) {
			lastSaveMetaRef.current = {};
		}
		try {
			(lastSaveMetaRef.current as any).payload_snapshot = snapshot ?? null;
		} catch {}
	}

	return {
		documento,
		pedido,
		paginas,
		objetos,
		log,
	};
}

/**
 * Gera payload completo para API /cdr/arquivos/salvar
 *
 * @param saveData - Dados do salvamento
 * @param saveDataRef - Ref dos dados
 * @param savedFilePathRef - Caminho salvo pelo Corel
 * @param lastSaveMetaRef - Metadados do último salvamento
 * @param isExternalUser - Se é usuário externo
 * @param role - Role do usuário
 * @param buildFilePath - Função para construir caminho
 * @param ensureCaminhoArquivo - Função para garantir caminho
 * @returns Payload para API ou null se dados insuficientes
 */
export function generateApiRequestPayload(options: {
	saveData: any;
	saveDataRef: any;
	savedFilePathRef: string;
	lastSaveMetaRef: any;
	isExternalUser: boolean;
	role?: string;
	buildFilePath?: (
		data: any,
		role: string,
		options?: any,
	) => { filePath: string } | null;
	ensureCaminhoArquivo?: (
		data: any,
		ref: string,
		builder?: any,
		role?: string,
	) => any;
}) {
	const {
		saveData,
		saveDataRef,
		savedFilePathRef,
		lastSaveMetaRef,
		isExternalUser,
		role,
		ensureCaminhoArquivo: ensureFunc,
	} = options;

	let workingData = saveData;
	if (!workingData) {
		// Tentar recuperar do storage
		try {
			const saved = readWithAliases<any>(
				Keys.export.currentSaveData,
				["currentSaveData"],
				{
					area: "session",
					persistIfAliased: true,
				},
			);
			if (saved) {
				workingData = saved;
				if (saveDataRef) saveDataRef.current = saved;
			} else {
				return null;
			}
		} catch {
			return null;
		}
	}

	// Forçar alinhamento do item selecionado
	try {
		const forcedItemIdRaw = readWithAliases<string>(
			Keys.export.selectedItemId,
			["currentSelectedItemId"],
			{
				area: "session",
				persistIfAliased: true,
			},
		);
		const forcedItemId = forcedItemIdRaw
			? parseInt(String(forcedItemIdRaw), 10)
			: NaN;
		if (!Number.isNaN(forcedItemId)) {
			if (!workingData.pedido) workingData.pedido = {} as any;
			(workingData.pedido as any).oit_id = forcedItemId;
		}
	} catch {}

	if (!workingData.pedido?.orc_id || !workingData.pedido?.oit_id) return null;

	// Garantir caminho_arquivo
	if (ensureFunc) {
		try {
			workingData = ensureFunc(
				workingData,
				savedFilePathRef,
				options.buildFilePath,
				role,
			);
		} catch {}
	}

	const caminhoArquivo =
		savedFilePathRef ||
		workingData.caminho_arquivo ||
		(isExternalUser
			? workingData.diretorios?.externo
			: workingData.diretorios?.interno) ||
		"";
	const contracaoGlobal = workingData.configuracoes?.contracao || "0px";
	const horarioEntrega = workingData.configuracoes?.entrega || "Hoje";
	const horarioCorte = normalizeToHHmm(
		workingData.regras?.horario_corte || "18:00",
	);
	const horarioEntregaGlobal = calcularHorarioEntrega(
		horarioEntrega,
		horarioCorte,
	);

	// Mapa de GUIDs
	const guidMap: Record<string, string> = {};
	if (
		lastSaveMetaRef?.current?.objetos &&
		Array.isArray(lastSaveMetaRef.current.objetos)
	) {
		lastSaveMetaRef.current.objetos.forEach((o: any) => {
			const sid = String(o?.staticId ?? o?.StaticID ?? "");
			const g = String(o?.guid ?? o?.Guid ?? "");
			if (sid && g) guidMap[sid] = g;
		});
	}

	const objetosMapeados =
		workingData.objetos?.map((obj: any, index: number) => {
			let objetoId = `obj-${index + 1}`;
			if (obj.staticId) objetoId = obj.staticId;
			else if (obj.StaticID) objetoId = obj.StaticID;

			let contracaoObjeto = 0;
			const normalizedContraction = obj.contraction
				? normalizeContraction(obj.contraction)
				: null;
			const normalizedGlobal =
				contracaoGlobal !== "CV" && contracaoGlobal !== "VARIÁVEL"
					? normalizeContraction(contracaoGlobal)
					: null;

			if (normalizedContraction) {
				contracaoObjeto = contractionToNumber(normalizedContraction);
			} else if (normalizedGlobal) {
				contracaoObjeto = contractionToNumber(normalizedGlobal);
			}

			return {
				objeto_id: objetoId,
				guid: guidMap[objetoId] ?? obj.guid ?? obj.Guid,
				nome: obj.nome || `Objeto ${index + 1}`,
				largura: parseFloat(obj.width) || 0,
				altura: parseFloat(obj.height) || 0,
				quantidade: parseInt(obj.quantity) || 1,
				contracao: contracaoObjeto,
				include: obj.include !== false,
				horario_entrega: horarioEntregaGlobal,
			};
		}) || [];

	// Resolver pro_id
	let final_pro_id = 0;
	if (Number(workingData?.regras?.pro_id) > 0) {
		final_pro_id = Number(workingData.regras.pro_id);
	} else {
		const fromProduto = Number(
			workingData?.produto?.pro_id ?? workingData?.produto?.id ?? 0,
		);
		if (fromProduto > 0) {
			final_pro_id = fromProduto;
		} else {
			try {
				const pedidoForLookup = readWithAliases<any>(
					Keys.order.currentPedido,
					["currentPedido"],
					{ area: "session", persistIfAliased: true },
				);
				const targetOitId = parseInt(
					String(workingData?.pedido?.oit_id || 0),
					10,
				);
				const itens = Array.isArray(pedidoForLookup?.itens)
					? pedidoForLookup.itens
					: [];
				const matchItem =
					itens.find((it: any) => Number(it?.oit_id) === targetOitId) ||
					itens[0];
				const fromPedido = Number(
					matchItem?.produto?.pro_id ?? matchItem?.produto?.id ?? 0,
				);
				if (fromPedido > 0) {
					final_pro_id = fromPedido;
				}
			} catch {}
		}
	}

	const prod_type = workingData.produto?.tipo
		? parseInt(workingData.produto.tipo, 10)
		: 1;

	// Gerar nome do arquivo
	const totalQtd =
		(Array.isArray(workingData.objetos)
			? workingData.objetos.reduce(
					(sum: number, o: any) => sum + (parseInt(o?.quantity) || 1),
					0,
				)
			: 0) || 1;
	const pedidoInfoForName = {
		orc_id: parseInt(workingData.pedido.orc_id, 10),
		oit_id: parseInt(workingData.pedido.oit_id, 10),
		cliente: {
			nome: workingData.cliente?.nome,
			nome_fantasia: workingData.cliente?.nome_fantasia,
		},
		material: { nome: workingData.material?.nome },
		produto: { nome: workingData.produto?.nome },
		regras: workingData.regras || {},
	} as any;
	const fileName = generateSafeFileName(
		pedidoInfoForName,
		totalQtd,
		contracaoGlobal,
		horarioEntrega.toUpperCase() === "URGENTE",
	);

	// Determinar caminho final
	let normalizedPath = caminhoArquivo;
	let finalFileNameForApi = fileName;
	try {
		if (savedFilePathRef && typeof savedFilePathRef === "string") {
			normalizedPath = savedFilePathRef;
			const last = extractFileName(normalizedPath);
			finalFileNameForApi = last.replace(/\.cdr$/i, "");
		} else {
			const lastSeg = extractFileName(caminhoArquivo || "");
			const hasFileInPath = /\.cdr$/i.test(lastSeg);
			const baseDir = hasFileInPath
				? String(caminhoArquivo).substring(
						0,
						String(caminhoArquivo).lastIndexOf("\\"),
					)
				: String(caminhoArquivo || "").replace(/\\+$/g, "");
			normalizedPath = (baseDir ? baseDir + "\\" : "") + fileName + ".CDR";
			finalFileNameForApi = fileName;
		}
	} catch {}

	return {
		orc_id: parseInt(workingData.pedido.orc_id, 10),
		oit_id: parseInt(workingData.pedido.oit_id, 10),
		nome_arquivo: finalFileNameForApi,
		caminho_arquivo: normalizedPath,
		sys_save: true,
		pro_id: final_pro_id,
		prod_type,
		sys_export: false,
		sys_update: isExternalUser
			? "Salvamento para cliente externo"
			: "Salvamento para produção",
		entrega_opcao: horarioEntrega,
		horario_corte: horarioCorte,
		objetos: objetosMapeados,

		// Metadados de integridade
		...(lastSaveMetaRef?.current
			? {
					hashAlgoritmo: lastSaveMetaRef.current.hashAlgoritmo,
					hashArquivo: lastSaveMetaRef.current.hashArquivo,
					tamanhoBytes: lastSaveMetaRef.current.tamanhoBytes,
					ultimaGravacaoUtc: lastSaveMetaRef.current.ultimaGravacaoUtc,
					integridade: {
						objetosVerificados:
							lastSaveMetaRef.current.integridade?.objetosVerificados || 0,
						metadadosOk: lastSaveMetaRef.current.integridade?.metadadosOk || 0,
						metadadosPerdidos:
							lastSaveMetaRef.current.integridade?.metadadosPerdidos || 0,
						...(lastSaveMetaRef.current.integridade?.objetosProcessados !==
						undefined
							? {
									objetosProcessados:
										lastSaveMetaRef.current.integridade.objetosProcessados,
								}
							: {}),
					},
				}
			: {}),

		// Métricas de save (C#)
		...(lastSaveMetaRef?.current?.metrics
			? { metrics: lastSaveMetaRef.current.metrics }
			: {}),

		// Snapshot de payload
		...(function () {
			try {
				const rawSnapshot: any = (lastSaveMetaRef?.current as any)
					?.payload_snapshot;

				// DEBUG: Log do snapshot antes de processar
				if (typeof sendToTerminal !== "undefined") {
					sendToTerminal("🔍 [DEBUG:PAYLOAD_SNAPSHOT] Processando snapshot", {
						hasSnapshot: !!rawSnapshot,
						snapshotType: typeof rawSnapshot,
						isSnapshotEmpty: rawSnapshot
							? Object.keys(rawSnapshot).length === 0
							: true,
						snapshotKeys: rawSnapshot
							? Object.keys(rawSnapshot).slice(0, 10)
							: [],
					});
				}

				if (!rawSnapshot) {
					// ❌ CRÍTICO: API requer payload_snapshot obrigatório!
					logger.error(
						"[PAYLOAD_BUILDER] payload_snapshot ausente! API retornará 400.",
					);
					return {};
				}
				const clone = JSON.parse(JSON.stringify(rawSnapshot));
				if (clone?.result && typeof clone.result === "object") {
					delete clone.result.notes;
				}
				return { payload_snapshot: clone };
			} catch (err) {
				logger.error(
					"[PAYLOAD_BUILDER] Erro ao processar payload_snapshot:",
					err,
				);
				return {};
			}
		})(),
	};
}

/**
 * Gera payload simplificado para debug/UI
 *
 * @param saveData - Dados do salvamento
 * @param fileInfo - Informações do arquivo
 * @param isExternalUser - Se é usuário externo
 * @param role - Role do usuário
 * @returns Payload de debug
 */
export function generateDebugPayload(
	saveData: any,
	fileInfo: { fileName: string; directory: string },
	isExternalUser: boolean,
	role?: string,
) {
	if (!saveData) return null;

	const totalObjetos =
		saveData.objetos?.reduce(
			(total: number, obj: any) => total + (obj.quantity || 1),
			0,
		) || 0;
	const totalArea = saveData.totalArea || 0;
	const metrosVendidos = saveData.metrosVendidos || 0;
	const diferencaMetragem = Math.max(0, metrosVendidos - totalArea);
	const percentualUsado =
		metrosVendidos > 0 ? ((totalArea / metrosVendidos) * 100).toFixed(2) : "0";

	return {
		pedido: {
			id: saveData.pedido.orc_id,
			itemId: saveData.pedido.oit_id,
			nomeArquivo: fileInfo.fileName,
			caminhoArquivo:
				saveData.caminho_arquivo ||
				(isExternalUser
					? saveData.diretorios.externo
					: saveData.diretorios.interno),
			dataSalvamento: new Date().toISOString(),
		},
		cliente: {
			nome: saveData.cliente?.nome,
			nomeFantasia: saveData.cliente?.nome_fantasia,
		},
		produto: { nome: saveData.produto?.nome, tipo: saveData.produto?.tipo },
		material: { nome: saveData.material?.nome },
		diretorios: {
			interno: saveData.diretorios?.interno,
			externo: saveData.diretorios?.externo,
		},
		configuracoes: {
			contracao: saveData.configuracoes?.contracao,
			entrega: saveData.configuracoes?.entrega,
		},
		medicoes: {
			totalObjetos,
			totalArea,
			metrosVendidos,
			diferencaMetragem,
			percentualUsado,
		},
		objetos: saveData.objetos?.map((obj: any, index: number) => ({
			id: obj.id || `obj-${index + 1}`,
			nome: obj.nome,
			nomeBase: obj.nome?.split("_")[0] || obj.nome,
			largura: obj.width,
			altura: obj.height,
			areaUnitaria: obj.totalArea / (obj.quantity || 1),
			quantidade: obj.quantity || 1,
			areaTotal: obj.totalArea,
			status: obj.status || "success",
		})),
		processo: { usuario: { tipo: role }, timestamp: new Date().toISOString() },
	};
}
